import { Component, ComponentProps, ComponentType, ErrorInfo, Suspense } from 'react'
import React from 'react'

import { BugsnagErrorBoundary } from '@bugsnag/plugin-react'

import logger from '@cash-web/shared/util-logger'

import ErrorFallbackComponent from './ErrorFallbackComponent/ErrorFallbackComponent'

const initialState: { hasError: false; error: Error; info: ErrorInfo } = {
  hasError: false,
  error: new Error(),
  info: { componentStack: '' },
}

type Props = ComponentProps<BugsnagErrorBoundary>
type FallbackProps = ComponentProps<NonNullable<Props['FallbackComponent']>>

/**
 * Error recovery renders synchronously, so a fallback that suspends — e.g. on translations that
 * have not loaded yet — throws unless a Suspense boundary sits between it and the error boundary.
 */
const withSuspense = (Fallback: ComponentType<FallbackProps>) => {
  const SuspendableFallback = (props: FallbackProps) => (
    <Suspense fallback={null}>
      <Fallback {...props} />
    </Suspense>
  )
  return SuspendableFallback
}

type PropsAndErrorBoundary = Props & {
  FallbackComponent: ComponentType<FallbackProps>
  MaybeBugSnagErrorBoundary: BugsnagErrorBoundary | undefined
}

class ErrorBoundaryInner extends Component<PropsAndErrorBoundary, typeof initialState> {
  constructor(props: PropsAndErrorBoundary) {
    super(props)
    this.state = initialState
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ ...this.state, error, info })
  }

  clearError = () => {
    this.setState(initialState)
  }

  override render() {
    const { FallbackComponent, MaybeBugSnagErrorBoundary } = this.props

    const children =
      MaybeBugSnagErrorBoundary !== undefined ? (
        <MaybeBugSnagErrorBoundary {...this.props} FallbackComponent={FallbackComponent} />
      ) : (
        this.props.children
      )

    return this.state.hasError ? (
      <FallbackComponent error={this.state.error} info={this.state.info} clearError={this.clearError} />
    ) : (
      children
    )
  }
}

export const ErrorBoundary = (props: Props) => {
  const MaybeBugSnagErrorBoundary = React.useMemo(() => logger.createErrorBoundary(), [])
  const FallbackComponent = React.useMemo(
    () => withSuspense(props.FallbackComponent ?? ErrorFallbackComponent),
    [props.FallbackComponent]
  )

  return (
    <ErrorBoundaryInner
      MaybeBugSnagErrorBoundary={MaybeBugSnagErrorBoundary}
      {...props}
      FallbackComponent={FallbackComponent}
    />
  )
}
